import { ChevronDownIcon, ShieldCheckIcon } from 'lucide-react';

import { useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { GroupMemberPermissionsDto } from '@splitfiesta/shared';
import { GROUP_MEMBER_PERMISSION_KEYS } from '@splitfiesta/shared';
import { Card, FormSwitch } from '@splitfiesta/ui';

interface GroupMemberPermissionsSectionProps {
  permissions: GroupMemberPermissionsDto;
  onPermissionChange: (
    key: keyof GroupMemberPermissionsDto,
    value: boolean
  ) => void;
  isLoading: boolean;
  isDeleting: boolean;
}

export function GroupMemberPermissionsSection({
  permissions,
  onPermissionChange,
  isLoading,
  isDeleting,
}: GroupMemberPermissionsSectionProps) {
  const { t } = useTranslation();

  // Starts collapsed: the form is already long, and most owners never
  // change the relaxed defaults.
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setExpanded(open => !open)}
          aria-expanded={expanded}
          aria-controls="member-permissions-panel"
          data-testid="member-permissions-toggle"
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ShieldCheckIcon className="w-5 h-5 mr-2" />
            {t('groups:memberPermissions.title')}
          </h3>
          <ChevronDownIcon
            aria-hidden="true"
            className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {expanded && (
          <div id="member-permissions-panel" className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('groups:memberPermissions.description')}
            </p>

            <div className="space-y-3 pt-1">
              {GROUP_MEMBER_PERMISSION_KEYS.map(key => (
                <FormSwitch
                  key={key}
                  id={`permission-${key}`}
                  label={t(`groups:memberPermissions.fields.${key}`)}
                  checked={permissions[key]}
                  onChange={value => onPermissionChange(key, value)}
                  disabled={isLoading || isDeleting}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default GroupMemberPermissionsSection;
